import React, { useState } from 'react';
import { X, Plus, Trash, AlertCircle } from 'lucide-react';
import { triggerTypes, uxTypes, segments, algorithmTypes, geos, devices, channels, frequencyCaps, backoffers, payoutModels } from '../data/mockData';
interface SecondClickOfferFormProps {
  mode: 'create' | 'edit';
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}
export const SecondClickOfferForm: React.FC<SecondClickOfferFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel
}) => {
  const [activeTab, setActiveTab] = useState('basics');
  const [formData, setFormData] = useState(initialData || {
    id: `sc_${Date.now()}`,
    status: 'active',
    name: '',
    triggerId: '',
    placementId: '',
    description: '',
    offerSource: {
      type: 'internal',
      productId: '',
      productName: '',
      regularPrice: 0,
      discountedPrice: 0,
      discountPercentage: 0
    },
    targeting: {
      geos: [],
      excludedGeos: [],
      devices: [],
      segments: [],
      fallbackType: 'persona'
    },
    pricingAndDiscounts: {
      dynamicIncentives: false,
      oneTimeBonus: '',
      countdown: {
        enabled: false,
        minutes: 15
      }
    },
    frequencyCaps: {
      typeId: 'per_day',
      value: 1,
      dontShowAfterViews: 3
    },
    creative: {
      title: '',
      imageUrl: '',
      copyVariants: [''],
      autoAB: true,
      weights: [100]
    },
    tracking: {
      clickId: '{click_id}',
      subid: '{subid}',
      postbackUrl: 'https://sinshift.com/postback?click_id={click_id}&status={status}'
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const tabs = [{
    id: 'basics',
    label: 'Basics'
  }, {
    id: 'offer',
    label: 'Offer Source'
  }, {
    id: 'targeting',
    label: 'Targeting'
  }, {
    id: 'pricing',
    label: 'Pricing & Discounts'
  }, {
    id: 'frequency',
    label: 'Frequency & Caps'
  }, {
    id: 'creative',
    label: 'Creative'
  }, {
    id: 'tracking',
    label: 'Tracking'
  }, {
    id: 'preview',
    label: 'Preview'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const isCheckbox = type === 'checkbox';
    const checked = (e.target as HTMLInputElement).checked;
    // Handle nested properties
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: isCheckbox ? checked : value
          }
        }));
      } else if (parts.length === 3) {
        const [parent, middle, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [middle]: {
              ...prev[parent][middle],
              [child]: isCheckbox ? checked : value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: isCheckbox ? checked : value
      }));
    }
    // Special handling for offer source type change
    if (name === 'offerSource.type') {
      if (value === 'internal') {
        setFormData(prev => ({
          ...prev,
          offerSource: {
            type: 'internal',
            productId: '',
            productName: '',
            regularPrice: 0,
            discountedPrice: 0,
            discountPercentage: 0
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          offerSource: {
            type: 'external',
            partnerId: '',
            partnerName: '',
            baseUrl: '',
            trackingParams: {
              utm_source: 'sinshift',
              utm_campaign: 'second_click',
              subid: '{subid}',
              click_id: '{click_id}'
            },
            payoutModelId: 'cpa',
            payoutAmount: 0
          }
        }));
      }
    }
  };
  const handleMultiSelect = (field: string, value: string, isSelected: boolean) => {
    // Handle nested properties for arrays
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => {
        const currentArray = [...(prev[parent][child] || [])];
        if (isSelected && !currentArray.includes(value)) {
          currentArray.push(value);
        } else if (!isSelected && currentArray.includes(value)) {
          const index = currentArray.indexOf(value);
          currentArray.splice(index, 1);
        }
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: currentArray
          }
        };
      });
    } else {
      setFormData(prev => {
        const currentArray = [...(prev[field] || [])];
        if (isSelected && !currentArray.includes(value)) {
          currentArray.push(value);
        } else if (!isSelected && currentArray.includes(value)) {
          const index = currentArray.indexOf(value);
          currentArray.splice(index, 1);
        }
        return {
          ...prev,
          [field]: currentArray
        };
      });
    }
  };
  const handleAddCopyVariant = () => {
    setFormData(prev => ({
      ...prev,
      creative: {
        ...prev.creative,
        copyVariants: [...prev.creative.copyVariants, ''],
        weights: [...prev.creative.weights, 100 / (prev.creative.copyVariants.length + 1)]
      }
    }));
  };
  const handleCopyVariantChange = (index: number, value: string) => {
    setFormData(prev => {
      const copyVariants = [...prev.creative.copyVariants];
      copyVariants[index] = value;
      return {
        ...prev,
        creative: {
          ...prev.creative,
          copyVariants
        }
      };
    });
  };
  const handleWeightChange = (index: number, value: number) => {
    setFormData(prev => {
      const weights = [...prev.creative.weights];
      weights[index] = value;
      return {
        ...prev,
        creative: {
          ...prev.creative,
          weights
        }
      };
    });
  };
  const handleRemoveCopyVariant = (index: number) => {
    setFormData(prev => {
      const copyVariants = [...prev.creative.copyVariants];
      const weights = [...prev.creative.weights];
      copyVariants.splice(index, 1);
      weights.splice(index, 1);
      // Redistribute weights
      if (weights.length > 0) {
        const totalWeight = weights.reduce((sum, w) => sum + w, 0);
        if (totalWeight !== 100) {
          const factor = 100 / totalWeight;
          weights.forEach((w, i) => {
            weights[i] = w * factor;
          });
        }
      }
      return {
        ...prev,
        creative: {
          ...prev.creative,
          copyVariants,
          weights
        }
      };
    });
  };
  const handleTrackingParamChange = (key: string, value: string) => {
    if (formData.offerSource.type === 'external') {
      setFormData(prev => ({
        ...prev,
        offerSource: {
          ...prev.offerSource,
          trackingParams: {
            ...prev.offerSource.trackingParams,
            [key]: value
          }
        }
      }));
    }
  };
  const handleAddTrackingParam = () => {
    if (formData.offerSource.type === 'external') {
      const newKey = prompt('Enter parameter name:');
      if (newKey && !formData.offerSource.trackingParams[newKey]) {
        setFormData(prev => ({
          ...prev,
          offerSource: {
            ...prev.offerSource,
            trackingParams: {
              ...prev.offerSource.trackingParams,
              [newKey]: ''
            }
          }
        }));
      }
    }
  };
  const handleRemoveTrackingParam = (key: string) => {
    if (formData.offerSource.type === 'external') {
      setFormData(prev => {
        const newTrackingParams = {
          ...prev.offerSource.trackingParams
        };
        delete newTrackingParams[key];
        return {
          ...prev,
          offerSource: {
            ...prev.offerSource,
            trackingParams: newTrackingParams
          }
        };
      });
    }
  };
  const calculateDiscount = () => {
    if (formData.offerSource.type === 'internal') {
      const regularPrice = parseFloat(formData.offerSource.regularPrice);
      const discountedPrice = parseFloat(formData.offerSource.discountedPrice);
      if (regularPrice > 0 && discountedPrice > 0) {
        const discountPercentage = (regularPrice - discountedPrice) / regularPrice * 100;
        setFormData(prev => ({
          ...prev,
          offerSource: {
            ...prev.offerSource,
            discountPercentage: Math.round(discountPercentage)
          }
        }));
      }
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Validate basic fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.triggerId) newErrors.triggerId = 'Trigger is required';
    if (!formData.placementId) newErrors.placementId = 'Placement is required';
    // Validate offer source
    if (formData.offerSource.type === 'internal') {
      if (!formData.offerSource.productName.trim()) {
        newErrors['offerSource.productName'] = 'Product name is required';
      }
      if (formData.offerSource.regularPrice <= 0) {
        newErrors['offerSource.regularPrice'] = 'Regular price must be greater than 0';
      }
      if (formData.offerSource.discountedPrice <= 0) {
        newErrors['offerSource.discountedPrice'] = 'Discounted price must be greater than 0';
      }
      if (formData.offerSource.discountedPrice >= formData.offerSource.regularPrice) {
        newErrors['offerSource.discountedPrice'] = 'Discounted price must be less than regular price';
      }
    } else {
      if (!formData.offerSource.partnerName.trim()) {
        newErrors['offerSource.partnerName'] = 'Partner name is required';
      }
      if (!formData.offerSource.baseUrl) {
        newErrors['offerSource.baseUrl'] = 'Base URL is required';
      }
      if (formData.offerSource.payoutAmount <= 0) {
        newErrors['offerSource.payoutAmount'] = 'Payout amount must be greater than 0';
      }
    }
    // Validate targeting
    if (formData.targeting.segments.length === 0) {
      newErrors['targeting.segments'] = 'At least one segment is required';
    }
    if (formData.targeting.geos.length === 0) {
      newErrors['targeting.geos'] = 'At least one GEO is required';
    }
    if (formData.targeting.devices.length === 0) {
      newErrors['targeting.devices'] = 'At least one device is required';
    }
    // Validate frequency caps
    if (formData.frequencyCaps.value <= 0) {
      newErrors['frequencyCaps.value'] = 'Frequency cap must be greater than 0';
    }
    if (formData.frequencyCaps.dontShowAfterViews <= 0) {
      newErrors['frequencyCaps.dontShowAfterViews'] = "Don't show after views must be greater than 0";
    }
    // Validate creative
    if (!formData.creative.title.trim()) {
      newErrors['creative.title'] = 'Title is required';
    }
    if (!formData.creative.imageUrl.trim()) {
      newErrors['creative.imageUrl'] = 'Image URL is required';
    }
    if (formData.creative.copyVariants.some(variant => !variant.trim())) {
      newErrors['creative.copyVariants'] = 'All copy variants must have content';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Find the first tab with an error and switch to it
      for (const tab of tabs) {
        const hasError = Object.keys(errors).some(key => {
          if (key.includes('.')) {
            const [parent] = key.split('.');
            return tab.id === parent;
          }
          return tab.id === key;
        });
        if (hasError) {
          setActiveTab(tab.id);
          break;
        }
      }
    }
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basics':
        return <div className="space-y-6" data-id="element-12442">
            <div data-id="element-12443">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12444">
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12445">
                <option value="active" data-id="element-12446">Active</option>
                <option value="inactive" data-id="element-12447">Inactive</option>
              </select>
            </div>
            <div data-id="element-12448">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12449">
                Offer Name*
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., Token Package Upsell" data-id="element-12450" />
              {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12451">
                  {errors.name}
                </p>}
            </div>
            <div data-id="element-12452">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12453">
                Trigger Type*
              </label>
              <select name="triggerId" value={formData.triggerId} onChange={handleChange} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border ${errors.triggerId ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white`} data-id="element-12454">
                <option value="" data-id="element-12455">Select a trigger</option>
                {triggerTypes.map(trigger => <option key={trigger.id} value={trigger.id} data-id="element-12456">
                    {trigger.name}
                  </option>)}
              </select>
              {errors.triggerId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12457">
                  {errors.triggerId}
                </p>}
            </div>
            <div data-id="element-12458">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12459">
                Placement Type*
              </label>
              <select name="placementId" value={formData.placementId} onChange={handleChange} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border ${errors.placementId ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white`} data-id="element-12460">
                <option value="" data-id="element-12461">Select a placement</option>
                {uxTypes.map(type => <option key={type.id} value={type.id} data-id="element-12462">
                    {type.name}
                  </option>)}
              </select>
              {errors.placementId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12463">
                  {errors.placementId}
                </p>}
            </div>
            <div data-id="element-12464">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12465">
                Description
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="Brief description of the offer" data-id="element-12466" />
            </div>
          </div>;
      case 'offer':
        return <div className="space-y-6" data-id="element-12467">
            <div data-id="element-12468">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12469">
                Offer Source Type
              </label>
              <select name="offerSource.type" value={formData.offerSource.type} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12470">
                <option value="internal" data-id="element-12471">Internal Product</option>
                <option value="external" data-id="element-12472">External Offer</option>
              </select>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12473">
                {formData.offerSource.type === 'internal' ? 'Offer an internal product or subscription from your own system' : 'Offer an external product from a partner network'}
              </p>
            </div>
            {formData.offerSource.type === 'internal' ? <>
                <div data-id="element-12474">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12475">
                    Product Name*
                  </label>
                  <input type="text" name="offerSource.productName" value={formData.offerSource.productName} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['offerSource.productName'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., 500 Tokens" data-id="element-12476" />
                  {errors['offerSource.productName'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12477">
                      {errors['offerSource.productName']}
                    </p>}
                </div>
                <div data-id="element-12478">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12479">
                    Product ID
                  </label>
                  <input type="text" name="offerSource.productId" value={formData.offerSource.productId} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="e.g., token_package_1" data-id="element-12480" />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12481">
                    Internal ID used to identify this product in your system
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-12482">
                  <div data-id="element-12483">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12484">
                      Regular Price*
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12485">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12486">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12487">$</span>
                      </div>
                      <input type="number" name="offerSource.regularPrice" value={formData.offerSource.regularPrice} onChange={e => {
                    handleChange(e);
                    setTimeout(calculateDiscount, 0);
                  }} className={`block w-full pl-7 pr-12 py-2 border ${errors['offerSource.regularPrice'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="0.00" step="0.01" min="0" data-id="element-12488" />
                    </div>
                    {errors['offerSource.regularPrice'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12489">
                        {errors['offerSource.regularPrice']}
                      </p>}
                  </div>
                  <div data-id="element-12490">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12491">
                      Discounted Price*
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12492">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12493">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12494">$</span>
                      </div>
                      <input type="number" name="offerSource.discountedPrice" value={formData.offerSource.discountedPrice} onChange={e => {
                    handleChange(e);
                    setTimeout(calculateDiscount, 0);
                  }} className={`block w-full pl-7 pr-12 py-2 border ${errors['offerSource.discountedPrice'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="0.00" step="0.01" min="0" data-id="element-12495" />
                    </div>
                    {errors['offerSource.discountedPrice'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12496">
                        {errors['offerSource.discountedPrice']}
                      </p>}
                  </div>
                  <div data-id="element-12497">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12498">
                      Discount Percentage
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12499">
                      <input type="number" name="offerSource.discountPercentage" value={formData.offerSource.discountPercentage} readOnly className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:text-white" data-id="element-12500" />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-12501">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12502">%</span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-12503">
                      Auto-calculated
                    </p>
                  </div>
                </div>
              </> : <>
                <div data-id="element-12504">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12505">
                    Partner Name*
                  </label>
                  <input type="text" name="offerSource.partnerName" value={formData.offerSource.partnerName} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['offerSource.partnerName'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., DateNow" data-id="element-12506" />
                  {errors['offerSource.partnerName'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12507">
                      {errors['offerSource.partnerName']}
                    </p>}
                </div>
                <div data-id="element-12508">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12509">
                    Partner ID
                  </label>
                  <input type="text" name="offerSource.partnerId" value={formData.offerSource.partnerId} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="e.g., partner_2" data-id="element-12510" />
                </div>
                <div data-id="element-12511">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12512">
                    Base URL*
                  </label>
                  <input type="url" name="offerSource.baseUrl" value={formData.offerSource.baseUrl} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['offerSource.baseUrl'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="https://example.com/dating" data-id="element-12513" />
                  {errors['offerSource.baseUrl'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12514">
                      {errors['offerSource.baseUrl']}
                    </p>}
                </div>
                <div data-id="element-12515">
                  <div className="flex justify-between items-center mb-2" data-id="element-12516">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-12517">
                      Tracking Parameters
                    </label>
                    <button type="button" onClick={handleAddTrackingParam} className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors" data-id="element-12518">
                      Add Parameter
                    </button>
                  </div>
                  <div className="space-y-2 border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12519">
                    {formData.offerSource.trackingParams && Object.entries(formData.offerSource.trackingParams).map(([key, value]) => <div key={key} className="flex items-center gap-2" data-id="element-12520">
                            <div className="flex-1 flex items-center gap-2" data-id="element-12521">
                              <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[100px]" data-id="element-12522">
                                {key}:
                              </span>
                              <input type="text" value={value as string} onChange={e => handleTrackingParamChange(key, e.target.value)} className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-12523" />
                            </div>
                            <button type="button" onClick={() => handleRemoveTrackingParam(key)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" data-id="element-12524">
                              <X size={16} data-id="element-12525" />
                            </button>
                          </div>)}
                    {!formData.offerSource.trackingParams || Object.keys(formData.offerSource.trackingParams).length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-12526">
                          No tracking parameters defined.
                        </p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12527">
                  <div data-id="element-12528">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12529">
                      Payout Model
                    </label>
                    <select name="offerSource.payoutModelId" value={formData.offerSource.payoutModelId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12530">
                      {payoutModels.map(model => <option key={model.id} value={model.id} data-id="element-12531">
                          {model.name} ({model.description})
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-12532">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12533">
                      Payout Amount*
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12534">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12535">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12536">$</span>
                      </div>
                      <input type="number" name="offerSource.payoutAmount" value={formData.offerSource.payoutAmount} onChange={handleChange} className={`block w-full pl-7 pr-12 py-2 border ${errors['offerSource.payoutAmount'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="0.00" step="0.01" min="0" data-id="element-12537" />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-12538">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12539">
                          {formData.offerSource.payoutModelId === 'revshare' ? '%' : ''}
                        </span>
                      </div>
                    </div>
                    {errors['offerSource.payoutAmount'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12540">
                        {errors['offerSource.payoutAmount']}
                      </p>}
                  </div>
                </div>
              </>}
          </div>;
      case 'targeting':
        return <div className="space-y-6" data-id="element-12541">
            <div data-id="element-12542">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12543">
                Segment Targeting*
              </label>
              <div className="border border-gray-300 dark:border-gray-700 rounded-md p-4" data-id="element-12544">
                {segments.map(segment => <div key={segment.id} className="flex items-center mb-3" data-id="element-12545">
                    <input type="checkbox" id={`segment-${segment.id}`} checked={formData.targeting.segments.includes(segment.id)} onChange={e => handleMultiSelect('targeting.segments', segment.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12546" />
                    <label htmlFor={`segment-${segment.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12547">
                      <span className="font-medium" data-id="element-12548">{segment.name}</span> -{' '}
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-12549">
                        {segment.description}
                      </span>
                    </label>
                  </div>)}
              </div>
              {errors['targeting.segments'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12550">
                  {errors['targeting.segments']}
                </p>}
            </div>
            <div data-id="element-12551">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12552">
                Fallback Strategy (when algorithm confidence {'<'} threshold)
              </label>
              <select name="targeting.fallbackType" value={formData.targeting.fallbackType} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12553">
                {algorithmTypes.map(type => <option key={type.id} value={type.id} data-id="element-12554">
                    {type.name} - {type.description}
                  </option>)}
              </select>
            </div>
            <div data-id="element-12555">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12556">
                GEO Targeting*
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12557">
                <div data-id="element-12558">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12559">
                    Include GEOs
                  </h4>
                  <div className="h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12560">
                    {geos.map(geo => <div key={geo.id} className="flex items-center mb-2" data-id="element-12561">
                        <input type="checkbox" id={`geo-${geo.id}`} checked={formData.targeting.geos.includes(geo.id)} onChange={e => handleMultiSelect('targeting.geos', geo.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12562" />
                        <label htmlFor={`geo-${geo.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12563">
                          {geo.name} ({geo.code}) - {geo.tier}
                        </label>
                      </div>)}
                  </div>
                  {errors['targeting.geos'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12564">
                      {errors['targeting.geos']}
                    </p>}
                </div>
                <div data-id="element-12565">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12566">
                    Exclude GEOs
                  </h4>
                  <div className="h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12567">
                    {geos.map(geo => <div key={geo.id} className="flex items-center mb-2" data-id="element-12568">
                        <input type="checkbox" id={`exclude-geo-${geo.id}`} checked={formData.targeting.excludedGeos.includes(geo.id)} onChange={e => handleMultiSelect('targeting.excludedGeos', geo.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12569" />
                        <label htmlFor={`exclude-geo-${geo.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12570">
                          {geo.name} ({geo.code})
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-12571">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12572">
                Device Targeting*
              </label>
              <div className="flex flex-wrap gap-4" data-id="element-12573">
                {devices.map(device => <div key={device.id} className="flex items-center" data-id="element-12574">
                    <input type="checkbox" id={`device-${device.id}`} checked={formData.targeting.devices.includes(device.id)} onChange={e => handleMultiSelect('targeting.devices', device.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12575" />
                    <label htmlFor={`device-${device.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12576">
                      {device.name}
                    </label>
                  </div>)}
              </div>
              {errors['targeting.devices'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12577">
                  {errors['targeting.devices']}
                </p>}
            </div>
          </div>;
      case 'pricing':
        return <div className="space-y-6" data-id="element-12578">
            <div className="flex items-center" data-id="element-12579">
              <input type="checkbox" id="dynamicIncentives" name="pricingAndDiscounts.dynamicIncentives" checked={formData.pricingAndDiscounts.dynamicIncentives} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12580" />
              <label htmlFor="dynamicIncentives" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12581">
                Enable Dynamic Incentives
              </label>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12582">
              Dynamic incentives adjust the offer based on user behavior and
              history.
            </p>
            <div data-id="element-12583">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12584">
                One-Time Bonus
              </label>
              <input type="text" name="pricingAndDiscounts.oneTimeBonus" value={formData.pricingAndDiscounts.oneTimeBonus} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="e.g., 100 Extra Tokens" data-id="element-12585" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12586">
                A special bonus included with this offer (leave empty for none)
              </p>
            </div>
            <div data-id="element-12587">
              <div className="flex items-center" data-id="element-12588">
                <input type="checkbox" id="countdownEnabled" name="pricingAndDiscounts.countdown.enabled" checked={formData.pricingAndDiscounts.countdown.enabled} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12589" />
                <label htmlFor="countdownEnabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12590">
                  Enable Countdown Timer
                </label>
              </div>
              {formData.pricingAndDiscounts.countdown.enabled && <div className="mt-3" data-id="element-12591">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12592">
                    Countdown Duration (minutes)
                  </label>
                  <input type="number" name="pricingAndDiscounts.countdown.minutes" value={formData.pricingAndDiscounts.countdown.minutes} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" min="1" max="60" data-id="element-12593" />
                </div>}
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md" data-id="element-12594">
              <div className="flex items-start" data-id="element-12595">
                <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" data-id="element-12596" />
                <div data-id="element-12597">
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300" data-id="element-12598">
                    Pricing Strategy Note
                  </h4>
                  <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400" data-id="element-12599">
                    For internal offers, the discount is calculated based on the
                    regular and discounted prices. For external offers, the
                    payout model determines how the partner is compensated.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 'frequency':
        return <div className="space-y-6" data-id="element-12600">
            <div data-id="element-12601">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12602">
                Frequency Cap Type
              </label>
              <select name="frequencyCaps.typeId" value={formData.frequencyCaps.typeId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12603">
                {frequencyCaps.map(cap => <option key={cap.id} value={cap.id} data-id="element-12604">
                    {cap.name}
                  </option>)}
              </select>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12605">
                Determines how often this offer can be shown to the same user
              </p>
            </div>
            <div data-id="element-12606">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12607">
                Maximum Show Count*
              </label>
              <input type="number" name="frequencyCaps.value" value={formData.frequencyCaps.value} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['frequencyCaps.value'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12608" />
              {errors['frequencyCaps.value'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12609">
                  {errors['frequencyCaps.value']}
                </p>}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12610">
                Maximum number of times this offer can be shown to the same user
                per {formData.frequencyCaps.typeId.replace('_', ' ')}
              </p>
            </div>
            <div data-id="element-12611">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12612">
                Don't Show After Views*
              </label>
              <input type="number" name="frequencyCaps.dontShowAfterViews" value={formData.frequencyCaps.dontShowAfterViews} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['frequencyCaps.dontShowAfterViews'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12613" />
              {errors['frequencyCaps.dontShowAfterViews'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12614">
                  {errors['frequencyCaps.dontShowAfterViews']}
                </p>}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12615">
                Stop showing this offer after it has been viewed this many times
                without conversion
              </p>
            </div>
          </div>;
      case 'creative':
        return <div className="space-y-6" data-id="element-12616">
            <div data-id="element-12617">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12618">
                Creative Title*
              </label>
              <input type="text" name="creative.title" value={formData.creative.title} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['creative.title'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., Limited Time Offer!" data-id="element-12619" />
              {errors['creative.title'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12620">
                  {errors['creative.title']}
                </p>}
            </div>
            <div data-id="element-12621">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12622">
                Image URL*
              </label>
              <input type="url" name="creative.imageUrl" value={formData.creative.imageUrl} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['creative.imageUrl'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="https://example.com/image.jpg" data-id="element-12623" />
              {errors['creative.imageUrl'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12624">
                  {errors['creative.imageUrl']}
                </p>}
            </div>
            <div data-id="element-12625">
              <div className="flex justify-between items-center mb-2" data-id="element-12626">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-12627">
                  Copy Variants*
                </label>
                <button type="button" onClick={handleAddCopyVariant} className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors" data-id="element-12628">
                  Add Variant
                </button>
              </div>
              {errors['creative.copyVariants'] && <p className="mb-2 text-sm text-red-600 dark:text-red-400" data-id="element-12629">
                  {errors['creative.copyVariants']}
                </p>}
              <div className="space-y-4" data-id="element-12630">
                {formData.creative.copyVariants.map((variant, index) => <div key={index} className="flex items-start gap-2" data-id="element-12631">
                    <div className="flex-1" data-id="element-12632">
                      <div className="flex justify-between items-center mb-1" data-id="element-12633">
                        <label htmlFor={`copy-variant-${index}`} className="block text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-12634">
                          Variant {index + 1}
                        </label>
                        <input type="number" value={formData.creative.weights[index]} onChange={e => handleWeightChange(index, parseFloat(e.target.value))} className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-white" min="0" max="100" step="1" data-id="element-12635" />
                      </div>
                      <div className="flex items-start gap-2" data-id="element-12636">
                        <textarea id={`copy-variant-${index}`} value={variant} onChange={e => handleCopyVariantChange(index, e.target.value)} className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" rows={2} placeholder="Enter copy text here..." data-id="element-12637" />
                        {formData.creative.copyVariants.length > 1 && <button type="button" onClick={() => handleRemoveCopyVariant(index)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-2" data-id="element-12638">
                            <Trash size={16} data-id="element-12639" />
                          </button>}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="flex items-center" data-id="element-12640">
              <input type="checkbox" id="autoAB" name="creative.autoAB" checked={formData.creative.autoAB} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12641" />
              <label htmlFor="autoAB" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12642">
                Enable Auto A/B Testing
              </label>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12643">
              Automatically optimize which copy variant is shown based on
              performance
            </p>
          </div>;
      case 'tracking':
        return <div className="space-y-6" data-id="element-12644">
            <div data-id="element-12645">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12646">
                Click ID Macro
              </label>
              <input type="text" name="tracking.clickId" value={formData.tracking.clickId} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="{click_id}" data-id="element-12647" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12648">
                The token that will be replaced with the actual click ID in
                tracking URLs
              </p>
            </div>
            <div data-id="element-12649">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12650">
                Sub ID Macro
              </label>
              <input type="text" name="tracking.subid" value={formData.tracking.subid} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="{subid}" data-id="element-12651" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12652">
                The token that will be replaced with the sub ID in tracking URLs
              </p>
            </div>
            <div data-id="element-12653">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12654">
                Postback URL
              </label>
              <input type="text" name="tracking.postbackUrl" value={formData.tracking.postbackUrl} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="https://sinshift.com/postback?click_id={click_id}&status={status}" data-id="element-12655" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-12656">
                URL that will be called when a conversion is registered
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md" data-id="element-12657">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2" data-id="element-12658">
                Available Macros
              </h4>
              <div className="grid grid-cols-2 gap-2" data-id="element-12659">
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12660">
                  {'{click_id}'} - Unique click identifier
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12661">
                  {'{subid}'} - Sub-ID for tracking
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12662">
                  {'{geo}'} - User's GEO code
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12663">
                  {'{device}'} - User's device type
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12664">
                  {'{trigger}'} - Trigger event type
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12665">
                  {'{status}'} - Conversion status
                </div>
              </div>
            </div>
          </div>;
      case 'preview':
        return <div className="space-y-6" data-id="element-12666">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12667">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12668">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12669">
                  Preview
                </h3>
              </div>
              <div className="p-6" data-id="element-12670">
                <div className="max-w-lg mx-auto" data-id="element-12671">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-12672">
                    {formData.placementId === 'full_screen_modal' && <div className="relative" data-id="element-12673">
                        {formData.creative.imageUrl && <img src={formData.creative.imageUrl} alt="Creative" className="w-full h-48 object-cover" data-id="element-12674" />}
                        <div className="p-6" data-id="element-12675">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-12676">
                            {formData.creative.title || 'Offer Title'}
                          </h3>
                          <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-12677">
                            {formData.creative.copyVariants[0] || 'Offer description goes here.'}
                          </p>
                          {formData.offerSource.type === 'internal' && <div className="flex items-center mb-4" data-id="element-12678">
                              <span className="line-through text-gray-500 dark:text-gray-400 mr-2" data-id="element-12679">
                                ${formData.offerSource.regularPrice.toFixed(2)}
                              </span>
                              <span className="text-xl font-bold text-green-600 dark:text-green-400" data-id="element-12680">
                                $
                                {formData.offerSource.discountedPrice.toFixed(2)}
                              </span>
                              {formData.offerSource.discountPercentage > 0 && <span className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-xs font-semibold px-2 py-1 rounded" data-id="element-12681">
                                  {formData.offerSource.discountPercentage}% OFF
                                </span>}
                            </div>}
                          {formData.pricingAndDiscounts.oneTimeBonus && <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded mb-4" data-id="element-12682">
                              <p className="text-sm text-yellow-800 dark:text-yellow-300" data-id="element-12683">
                                <span className="font-bold" data-id="element-12684">Bonus:</span>{' '}
                                {formData.pricingAndDiscounts.oneTimeBonus}
                              </p>
                            </div>}
                          {formData.pricingAndDiscounts.countdown.enabled && <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md mb-4" data-id="element-12685">
                              <p className="text-sm text-gray-700 dark:text-gray-300 text-center" data-id="element-12686">
                                Offer expires in:{' '}
                                <span className="font-bold" data-id="element-12687">
                                  {formData.pricingAndDiscounts.countdown.minutes}
                                  :00
                                </span>
                              </p>
                            </div>}
                          <div className="flex space-x-3" data-id="element-12688">
                            <button className="flex-1 bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition-colors" data-id="element-12689">
                              Get This Offer
                            </button>
                            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-12690">
                              No Thanks
                            </button>
                          </div>
                        </div>
                      </div>}
                    {formData.placementId === 'bottom_sheet' && <div className="p-4" data-id="element-12691">
                        <div className="flex items-center mb-3" data-id="element-12692">
                          {formData.creative.imageUrl && <img src={formData.creative.imageUrl} alt="Creative" className="w-16 h-16 object-cover rounded-md mr-4" data-id="element-12693" />}
                          <div data-id="element-12694">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-12695">
                              {formData.creative.title || 'Offer Title'}
                            </h3>
                            {formData.offerSource.type === 'internal' && <div className="flex items-center" data-id="element-12696">
                                <span className="line-through text-gray-500 dark:text-gray-400 text-sm mr-2" data-id="element-12697">
                                  $
                                  {formData.offerSource.regularPrice.toFixed(2)}
                                </span>
                                <span className="text-green-600 dark:text-green-400 font-bold" data-id="element-12698">
                                  $
                                  {formData.offerSource.discountedPrice.toFixed(2)}
                                </span>
                              </div>}
                          </div>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm mb-3" data-id="element-12699">
                          {formData.creative.copyVariants[0] || 'Offer description goes here.'}
                        </p>
                        <div className="flex space-x-2" data-id="element-12700">
                          <button className="flex-1 bg-purple-600 text-white py-2 px-3 rounded text-sm hover:bg-purple-700 transition-colors" data-id="element-12701">
                            Get Offer
                          </button>
                          <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-12702">
                            Dismiss
                          </button>
                        </div>
                      </div>}
                    {formData.placementId === 'toast_with_cta' && <div className="p-3 flex items-center" data-id="element-12703">
                        {formData.creative.imageUrl && <img src={formData.creative.imageUrl} alt="Creative" className="w-10 h-10 object-cover rounded-md mr-3" data-id="element-12704" />}
                        <div className="flex-1" data-id="element-12705">
                          <h3 className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-12706">
                            {formData.creative.title || 'Offer Title'}
                          </h3>
                          <p className="text-xs text-gray-700 dark:text-gray-300" data-id="element-12707">
                            {formData.creative.copyVariants[0] || 'Offer description goes here.'}
                          </p>
                        </div>
                        <button className="ml-2 bg-purple-600 text-white py-1 px-2 rounded text-xs hover:bg-purple-700 transition-colors" data-id="element-12708">
                          View
                        </button>
                      </div>}
                    {formData.placementId === 'interstitial' && <div className="relative" data-id="element-12709">
                        {formData.creative.imageUrl && <img src={formData.creative.imageUrl} alt="Creative" className="w-full h-64 object-cover" data-id="element-12710" />}
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center p-6" data-id="element-12711">
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-12712">
                            {formData.creative.title || 'Offer Title'}
                          </h3>
                          <p className="text-white text-center mb-4 max-w-md" data-id="element-12713">
                            {formData.creative.copyVariants[0] || 'Offer description goes here.'}
                          </p>
                          <div className="flex space-x-3" data-id="element-12714">
                            <button className="bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 transition-colors" data-id="element-12715">
                              Get Offer
                            </button>
                            <button className="bg-gray-800 bg-opacity-50 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors" data-id="element-12716">
                              Continue
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12717">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-12718">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-12719">
                  Offer Summary
                </h3>
              </div>
              <div className="p-6" data-id="element-12720">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6" data-id="element-12721">
                  <div data-id="element-12722">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12723">
                      Name
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12724">
                      {formData.name}
                    </dd>
                  </div>
                  <div data-id="element-12725">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12726">
                      Status
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12727">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${formData.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-12728">
                        {formData.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </dd>
                  </div>
                  <div data-id="element-12729">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12730">
                      Trigger
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12731">
                      {triggerTypes.find(t => t.id === formData.triggerId)?.name || formData.triggerId}
                    </dd>
                  </div>
                  <div data-id="element-12732">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12733">
                      Placement
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12734">
                      {uxTypes.find(t => t.id === formData.placementId)?.name || formData.placementId}
                    </dd>
                  </div>
                  <div data-id="element-12735">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12736">
                      Offer Type
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12737">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${formData.offerSource.type === 'internal' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'}`} data-id="element-12738">
                        {formData.offerSource.type === 'internal' ? 'Internal' : 'External'}
                      </span>
                    </dd>
                  </div>
                  <div data-id="element-12739">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12740">
                      {formData.offerSource.type === 'internal' ? 'Product' : 'Partner'}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12741">
                      {formData.offerSource.type === 'internal' ? formData.offerSource.productName : formData.offerSource.partnerName}
                    </dd>
                  </div>
                  <div data-id="element-12742">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12743">
                      Target Segments
                    </dt>
                    <dd className="mt-1 text-sm" data-id="element-12744">
                      <div className="flex flex-wrap gap-1" data-id="element-12745">
                        {formData.targeting.segments.map(segment => <span key={segment} className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 rounded" data-id="element-12746">
                            {segment.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>)}
                      </div>
                    </dd>
                  </div>
                  <div data-id="element-12747">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-12748">
                      Frequency Cap
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white" data-id="element-12749">
                      {formData.frequencyCaps.value} per{' '}
                      {formData.frequencyCaps.typeId.replace('_', ' ')}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12750">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center" data-id="element-12751">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-12752">
          {mode === 'create' ? 'Create Second-Click Offer' : 'Edit Second-Click Offer'}
        </h2>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-12753">
          <X size={20} data-id="element-12754" />
        </button>
      </div>
      <div className="p-6" data-id="element-12755">
        <div className="mb-6 overflow-x-auto" data-id="element-12756">
          <div className="flex space-x-2" data-id="element-12757">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm font-medium rounded-md ${activeTab === tab.id ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-12758">
                {tab.label}
              </button>)}
          </div>
        </div>
        <form onSubmit={handleSubmit} data-id="element-12759">
          {renderTabContent()}
          <div className="mt-8 flex justify-end space-x-3" data-id="element-12760">
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-12761">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-12762">
              {mode === 'create' ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};